import type { PreferredTiming, RecentResult } from '@shared/leadIntake';
import type { LeadActivity } from '@shared/leadManagement';
import type { LeadPhoneContext } from '@shared/leadPhoneContext';
import type {
  LeadAttendanceStatus,
  LeadClassPlan,
  LeadWorkflow,
  LeadWorkflowTaskId,
  LeadWorkflowTaskState,
} from '@shared/leadWorkflowContract';
import type {
  MathCheckOfferAcceptance,
  MathCheckVerification,
} from '@shared/mathCheckQualification';
import type { PartnerAffiliateAccessCode } from '@shared/partnerContract';
import type { PaymentChannel } from '@shared/paymentChannels';
import type { LeadTrialFee } from '@shared/trialFees';
import type {
  ChemistryCourse,
  SubjectStreams,
  TuitionSubject,
} from '@shared/tuitionSubjects/catalog';

import { fetchWithConfig } from './apiUtils';

export interface RecordTrialFeeInput {
  amount: number;
  channel: PaymentChannel;
  receivedOn: string;
  notes?: string;
}

export interface ProInterestPayload {
  recentResult?: RecentResult;
  preferredTimings?: PreferredTiming[];
  mathCheckToken?: string;
  mathCheckConsent?: boolean;
  mathCheckQualification?: MathCheckOfferAcceptance;
  mathCheckVerification?: MathCheckVerification;
  name: string;
  contactNumber: string;
  level: string;
  email?: string | null;
  note?: string;
  subjects?: string[] | null;
  tuitionSubjects?: TuitionSubject[];
  subjectStreams?: SubjectStreams;
  chemistryCourse?: ChemistryCourse | null;
  attribution?: unknown;
  journey?: unknown;
  sourceLocation?: string | null;
  campaign_intent?: string | null;
}

export type ProInterestResponse =
  | { id: string; existingContact?: false }
  | { existingContact: true; id?: never };

export type ProInterestStatusResponse =
  | { submitted: false }
  | { submitted: true; at: string };

interface AttributionData {
  source?: string;
  medium?: string;
  campaign?: string;
  [key: string]: unknown;
}

interface JourneyData {
  [key: string]: unknown;
}

interface Partner {
  /** Both exist during the rolling compatibility window. */
  id?: string;
  _id?: string;
  slug: string;
  name: string;
  successEvent?: string;
  active?: boolean;
  affiliateUserId?: string | null;
  affiliateAccessCodeId?: string | null;
  affiliateEmail?: string | null;
  affiliateAccessCode?: PartnerAffiliateAccessCode | null;
  [key: string]: unknown;
}

interface CreatePartnerData {
  slug: string;
  name: string;
  successEvent?: string;
  active?: boolean;
  /** `null` clears the link; omitting the key leaves it alone. */
  affiliateUserId?: string | null;
  affiliateAccessCodeId?: string | null;
}

interface UpdatePartnerData {
  name?: string;
  successEvent?: string;
  active?: boolean;
  affiliateUserId?: string | null;
  affiliateAccessCodeId?: string | null;
}

interface PartnerResponse {
  partner: Partner;
}

interface PartnersResponse {
  partners: Partner[];
}

interface LeadsQueryParams {
  queue?: string;
  view?: 'all' | 'open' | 'flagged';
  level?: string;
  entry?: string;
  action?: string;
  followUp?: string;
  focus?: string;
  id?: string;
  status?: string;
  search?: string;
  page?: string;
  limit?: string;
  sortBy?: string;
  sortOrder?: string;
  attributedPartnerId?: string;
  partner?: string;
  nextTask?: string;
  overdue?: string;
  holdState?: string;
  attendanceStatus?: string;
  classPlan?: string;
  trialCodeIssued?: string;
  hidden?: string;
  tuitionSubject?: TuitionSubject;
}

interface Lead {
  _id: string;
  status: string;
  phoneContext?: LeadPhoneContext | null;
  workflow?: LeadWorkflow;
  [key: string]: unknown;
}

interface LeadsResponse {
  leads: Lead[];
  pagination: Record<string, unknown>;
  [key: string]: unknown;
}

interface ManualLeadData {
  name: string;
  studentName?: string;
  parentPreferredName?: string | null;
  studentShortName?: string | null;
  contactNumber: string;
  email?: string | null;
  note?: string;
  level?: string | null;
  secondaryStream?: string | null;
  subjects?: string[];
  tuitionSubjects?: TuitionSubject[];
  subjectStreams?: SubjectStreams;
  chemistryCourse?: ChemistryCourse | null;
  /** `null` removes the partner credit; omitting the key leaves it alone. */
  attributedPartnerId?: string | null;
  /** Create only: fail instead of updating a lead with this contact and student. */
  createOnly?: true;
}

interface ManualLeadResponse {
  lead: Lead;
  warnings?: string[];
}

interface TrialAccessCodeData {
  classCodeId?: string | null;
  studentName?: string | null;
  studentShortName?: string | null;
  secondaryStream?: string | null;
  subjects?: string[];
  tuitionSubjects?: TuitionSubject[];
  subjectStreams?: SubjectStreams;
  chemistryCourse?: ChemistryCourse | null;
}

interface TrialAccessCodeResponse {
  code: string;
  existing: boolean;
  lead: Lead;
  accessCode: Record<string, unknown>;
}

interface LeadWorkflowTaskPatch {
  id: LeadWorkflowTaskId;
  state?: LeadWorkflowTaskState;
  completedAt?: string | Date | null;
  notes?: string | null;
}

interface LeadWorkflowPatch {
  tasks?: LeadWorkflowTaskPatch[];
  hold?: {
    active?: boolean;
    followUpAt?: string | Date | null;
    reason?: string | null;
  };
  /**
   * The class code, label and teacher name are derived server-side from the
   * class the trial is booked into and are rejected if sent.
   */
  trial?: {
    classCodeId?: string | null;
    scheduledAt?: string | Date | null;
    timezone?: string;
    attendanceStatus?: LeadAttendanceStatus;
    classPlan?: LeadClassPlan;
  };
  internalNotes?: string | null;
}

interface LeadWorkflowResponse {
  message?: string;
  lead: Lead;
}

interface LeadVisibilityPatch {
  hidden: boolean;
  note?: string | null;
}

interface LeadVisibilityResponse {
  message: string;
  changed: boolean;
  lead: {
    _id: string;
    hiddenAt?: string | null;
    hiddenBy?: Record<string, unknown> | null;
    hiddenNote?: string | null;
  };
}

interface LeadTrialCleanupResponse {
  cleaned: boolean;
  reason: string;
  accessCode: Record<string, unknown> | null;
}

interface LeadTrialExpireResponse {
  outcome: 'code_expired' | 'entitlement_expired';
  cleaned?: boolean;
  reason?: string;
  userId?: string;
  expiresAt?: string;
  accessCode?: Record<string, unknown> | null;
}

interface UpdateLeadStatusOptions {
  notes?: string;
  [key: string]: unknown;
}

export interface ConvertLeadToEnrollmentData {
  enrollmentId: string;
  dealValue?: number | null;
}

/**
 * What the conversion actually did to the account. `mode` is decided
 * server-side: a trial student has a redeemed code, so it is normally `swap`.
 */
export interface ConvertLeadToEnrollmentLink {
  enrollmentId: string;
  mode: 'swap' | 'adopt';
  linkedUserId: string;
  linkedAccessCode: string | null;
  accountActivated: boolean;
}

export interface ConvertLeadToEnrollmentResponse {
  message?: string;
  link: ConvertLeadToEnrollmentLink;
  lead: Lead;
}

export const leadsApi = {
  async getAffiliateAccessCodes(
    token: string,
    search = ''
  ): Promise<{ accessCodes: PartnerAffiliateAccessCode[] }> {
    return fetchWithConfig(
      `/admin/partners/affiliate-access-codes?${new URLSearchParams({ search })}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async updateManagement(
    token: string,
    leadId: string,
    data: Record<string, unknown>
  ): Promise<{ lead: Lead; warnings?: string[] }> {
    return fetchWithConfig(`/admin/leads/${leadId}/management`, {
      method: 'PATCH',
      authToken: token,
      retry: false,
      body: JSON.stringify(data),
    });
  },
  /** Notes, or the rest of the history, newest first in pages of 50. */
  async getActivity(
    token: string,
    leadId: string,
    { kind, before }: { kind?: 'note' | 'history'; before?: string } = {}
  ): Promise<{ entries: LeadActivity[]; nextCursor: string | null }> {
    const query = new URLSearchParams({
      ...(kind && { kind }),
      ...(before && { before }),
    }).toString();
    return fetchWithConfig(
      `/admin/leads/${leadId}/activity${query ? `?${query}` : ''}`,
      { method: 'GET', authToken: token }
    );
  },
  async getSources(token: string, leadId: string): Promise<{ clicks: Lead[] }> {
    return fetchWithConfig(`/admin/leads/${leadId}/sources`, {
      method: 'GET',
      authToken: token,
    });
  },
  async recordTrialFee(
    token: string,
    leadId: string,
    data: RecordTrialFeeInput
  ): Promise<{ trialFee: LeadTrialFee }> {
    return fetchWithConfig(`/admin/leads/${leadId}/trial-fee`, {
      method: 'POST',
      authToken: token,
      retry: false,
      body: JSON.stringify(data),
    });
  },

  async setTrialFeeRequired(
    token: string,
    leadId: string,
    required: boolean
  ): Promise<{ trialFeeRequired: boolean }> {
    return fetchWithConfig(
      `/admin/leads/${encodeURIComponent(leadId)}/trial-fee`,
      {
        method: 'PATCH',
        authToken: token,
        retry: false,
        body: JSON.stringify({ required }),
      }
    );
  },

  async voidTrialFee(
    token: string,
    leadId: string,
    trialFeeId: string,
    voidReason?: string
  ): Promise<{ trialFee: LeadTrialFee | null }> {
    return fetchWithConfig(`/admin/leads/${leadId}/trial-fee/${trialFeeId}`, {
      method: 'DELETE',
      authToken: token,
      body: JSON.stringify({ voidReason }),
    });
  },

  async submitProInterest(
    payload: ProInterestPayload,
    token: string | null = null
  ): Promise<ProInterestResponse> {
    return fetchWithConfig<ProInterestResponse>('/leads/pro-interest', {
      method: 'POST',
      authToken: token ?? undefined,
      body: JSON.stringify(payload),
    });
  },

  async getMyProInterestStatus(
    token: string
  ): Promise<ProInterestStatusResponse> {
    return fetchWithConfig<ProInterestStatusResponse>(
      '/leads/pro-interest/my',
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async trackWhatsAppClick(
    attributionData: AttributionData,
    pageUrl: string,
    sourceLocation: string,
    journeyData: JourneyData | null = null
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig('/leads/whatsapp-click', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attribution: attributionData,
        ...(journeyData && { journey: journeyData }),
        pageUrl,
        sourceLocation,
      }),
    });
  },

  async trackRefClick(
    ref: string,
    landingPage: string | null,
    referrer: string | null
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig('/leads/ref-click', {
      method: 'POST',
      // The browser-session marker intentionally prefers one missed count over
      // two rows when the first response is lost after the server inserts it.
      retry: false,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ref,
        ...(landingPage && { landingPage }),
        ...(referrer && { referrer }),
      }),
    });
  },

  async getPartners(token: string): Promise<PartnersResponse> {
    return fetchWithConfig<PartnersResponse>('/admin/partners', {
      method: 'GET',
      authToken: token,
    });
  },

  async createPartner(
    token: string,
    data: CreatePartnerData
  ): Promise<PartnerResponse> {
    return fetchWithConfig<PartnerResponse>('/admin/partners', {
      method: 'POST',
      authToken: token,
      body: JSON.stringify(data),
    });
  },

  async updatePartner(
    token: string,
    id: string,
    data: UpdatePartnerData
  ): Promise<PartnerResponse> {
    return fetchWithConfig<PartnerResponse>(`/admin/partners/${id}`, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify(data),
    });
  },

  async deactivatePartner(token: string, id: string): Promise<PartnerResponse> {
    return fetchWithConfig<PartnerResponse>(`/admin/partners/${id}`, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify({ active: false }),
    });
  },

  async getLeads(
    token: string,
    params: LeadsQueryParams = {}
  ): Promise<LeadsResponse> {
    const queryParams = new URLSearchParams();
    for (const key of [
      'queue',
      'view',
      'level',
      'entry',
      'action',
      'followUp',
      'focus',
      'id',
    ] as const)
      if (params[key]) queryParams.append(key, params[key]!);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.attributedPartnerId) {
      queryParams.append('attributedPartnerId', params.attributedPartnerId);
    }
    if (params.partner) {
      queryParams.append('partner', params.partner);
    }
    if (params.nextTask) {
      queryParams.append('nextTask', params.nextTask);
    }
    if (params.overdue) queryParams.append('overdue', params.overdue);
    if (params.holdState) queryParams.append('holdState', params.holdState);
    if (params.attendanceStatus) {
      queryParams.append('attendanceStatus', params.attendanceStatus);
    }
    if (params.classPlan) queryParams.append('classPlan', params.classPlan);
    if (params.trialCodeIssued) {
      queryParams.append('trialCodeIssued', params.trialCodeIssued);
    }
    if (params.hidden) queryParams.append('hidden', params.hidden);
    if (params.tuitionSubject)
      queryParams.append('tuitionSubject', params.tuitionSubject);

    const queryString = queryParams.toString();
    const endpoint = `/admin/leads${queryString ? `?${queryString}` : ''}`;

    return fetchWithConfig<LeadsResponse>(endpoint, {
      method: 'GET',
      authToken: token,
    });
  },

  async createManualLead(
    token: string,
    data: ManualLeadData
  ): Promise<ManualLeadResponse> {
    return fetchWithConfig<ManualLeadResponse>('/admin/leads', {
      method: 'POST',
      authToken: token,
      body: JSON.stringify(data),
    });
  },

  async updateManualLead(
    token: string,
    leadId: string,
    data: ManualLeadData
  ): Promise<ManualLeadResponse> {
    return fetchWithConfig<ManualLeadResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}`,
      {
        method: 'PATCH',
        authToken: token,
        body: JSON.stringify(data),
      }
    );
  },

  async setLeadHidden(
    token: string,
    leadId: string,
    data: LeadVisibilityPatch
  ): Promise<LeadVisibilityResponse> {
    return fetchWithConfig<LeadVisibilityResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/hidden`,
      {
        method: 'PATCH',
        authToken: token,
        body: JSON.stringify({
          hidden: data.hidden,
          ...(data.note !== undefined && { note: data.note }),
        }),
      }
    );
  },

  async provisionTrialAccessCode(
    token: string,
    leadId: string,
    data: TrialAccessCodeData
  ): Promise<TrialAccessCodeResponse> {
    return fetchWithConfig<TrialAccessCodeResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/trial-access-code`,
      {
        method: 'POST',
        authToken: token,
        body: JSON.stringify(data),
      }
    );
  },

  async updateLeadWorkflow(
    token: string,
    leadId: string,
    data: LeadWorkflowPatch
  ): Promise<LeadWorkflowResponse> {
    return fetchWithConfig<LeadWorkflowResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/workflow`,
      {
        method: 'PATCH',
        authToken: token,
        body: JSON.stringify(data),
      }
    );
  },

  async cleanupTrialAccessCode(
    token: string,
    leadId: string
  ): Promise<LeadTrialCleanupResponse> {
    return fetchWithConfig<LeadTrialCleanupResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/trial-cleanup`,
      {
        method: 'POST',
        authToken: token,
      }
    );
  },

  async expireLeadTrial(
    token: string,
    leadId: string
  ): Promise<LeadTrialExpireResponse> {
    return fetchWithConfig<LeadTrialExpireResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/trial-expire`,
      {
        method: 'POST',
        authToken: token,
      }
    );
  },

  /**
   * POST /admin/leads/:id/convert-to-enrollment — the trial family said yes.
   * Links their existing account to the enrollment, marks the lead paid (Meta
   * CAPI Purchase included) and closes its checklist, in one call. The student
   * account is resolved server-side from the lead's own trial code.
   */
  async convertLeadToEnrollment(
    token: string,
    leadId: string,
    data: ConvertLeadToEnrollmentData
  ): Promise<ConvertLeadToEnrollmentResponse> {
    return fetchWithConfig<ConvertLeadToEnrollmentResponse>(
      `/admin/leads/${encodeURIComponent(leadId)}/convert-to-enrollment`,
      {
        method: 'POST',
        authToken: token,
        body: JSON.stringify({
          enrollmentId: data.enrollmentId,
          ...(data.dealValue !== undefined && { dealValue: data.dealValue }),
        }),
      }
    );
  },

  async updateLeadStatus(
    token: string,
    leadId: string,
    leadStatus: string,
    options: UpdateLeadStatusOptions = {}
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig(`/admin/leads/${leadId}/status`, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify({
        leadStatus,
        ...options,
      }),
    });
  },
};
